/**
 * Utilidades para calcular el estado de apertura de los negocios en el frontend
 */

interface OperatingHours {
  [key: string]: {
    open?: string
    close?: string
    closed?: boolean
  }
}

/**
 * Calcula si un negocio está abierto basándose en los horarios de operación
 * @param operatingHours - Horarios de operación del negocio
 * @param isActive - Si el negocio está activo manualmente
 * @returns true si el negocio está abierto, false si está cerrado
 */
export function calculateStoreIsOpen(
  operatingHours: OperatingHours | null | undefined,
  isActive: boolean = true
): boolean {
  // Si el negocio no está activo manualmente, está cerrado
  if (!isActive) {
    return false
  }

  // Si no hay horarios configurados, asumir que está abierto (comportamiento por defecto)
  if (!operatingHours || typeof operatingHours !== 'object') {
    return true
  }

  // Obtener día actual (0 = domingo, 1 = lunes, ..., 6 = sábado)
  const now = new Date()
  const currentDay = now.getDay() // 0-6
  const currentTime = now.getHours() * 60 + now.getMinutes() // Tiempo en minutos desde medianoche

  // Mapear día de la semana a nombre en inglés
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const currentDayName = dayNames[currentDay]

  // Obtener horarios del día actual
  const todayHours = operatingHours[currentDayName]

  // Si no hay horarios para este día o está marcado como cerrado
  if (!todayHours || todayHours.closed === true) {
    return false
  }

  // Si no hay horarios de apertura/cierre, asumir que está abierto
  if (!todayHours.open || !todayHours.close) {
    return true
  }

  // Convertir horarios a minutos desde medianoche
  const parseTime = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return hours * 60 + minutes
  }

  try {
    const openTime = parseTime(todayHours.open)
    const closeTime = parseTime(todayHours.close)

    // Verificar si la hora actual está dentro del rango de horarios
    return currentTime >= openTime && currentTime < closeTime
  } catch (error) {
    console.error('Error parsing operating hours:', error)
    // Si hay error al parsear, asumir que está abierto
    return true
  }
}

/**
 * Obtiene el próximo horario de apertura del negocio
 * @param operatingHours - Horarios de operación del negocio
 * @returns Objeto con el día y hora de la próxima apertura, o null si no hay horarios
 */
export function getNextOpeningTime(
  operatingHours: OperatingHours | null | undefined
): { day: string; time: string } | null {
  if (!operatingHours || typeof operatingHours !== 'object') {
    return null
  }

  const now = new Date()
  const currentDay = now.getDay()
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const dayLabels = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']

  // Buscar el próximo día abierto (empezando desde hoy)
  for (let i = 0; i < 7; i++) {
    const dayIndex = (currentDay + i) % 7
    const dayName = dayNames[dayIndex]
    const dayHours = operatingHours[dayName]

    if (dayHours && !dayHours.closed && dayHours.open) {
      // Si es hoy y aún no ha cerrado, retornar null (ya está abierto o abrirá hoy)
      if (i === 0) {
        const currentTime = now.getHours() * 60 + now.getMinutes()
        const openTime = dayHours.open.split(':').map(Number)
        const openTimeMinutes = openTime[0] * 60 + openTime[1]

        if (currentTime < openTimeMinutes) {
          return { day: dayLabels[dayIndex], time: dayHours.open }
        }
        // Si ya pasó la hora de apertura de hoy, buscar el siguiente día
        continue
      }

      return { day: dayLabels[dayIndex], time: dayHours.open }
    }
  }

  return null
}

